import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useAppContext } from '../../../Store/Store';

type DynNS = any;

const ENV = (import.meta as any).env || {};
const LICENSE = ENV.VITE_DBR_LICENSE || '';
const STATIC_URL = (ENV.VITE_STATIC_URL || '').replace(/\/$/, '');
const SDK_DIR = 'assets/dbr/dynamsoft-barcode-reader-js-11.0.6000.250916/dist';

interface CodeScannerScreenProps {
  onNext: () => void;
  showSkipNextScreenButton?: boolean;
  showBackButton?: boolean;
  onBack?: () => void;
}

export default function CodeScannerScreen({
  onNext,
  showSkipNextScreenButton = false,
  showBackButton = false,
  onBack,
}: CodeScannerScreenProps) {
  const { backendBaseUrl, redirectBaseUrl, product, event, style } =
    useAppContext();
  const [status, setStatus] = useState<
    'idle' | 'loading' | 'ready' | 'scanning' | 'posting'
  >('idle');
  const [error, setError] = useState<string>('');
  const [fieldKey, setFieldKey] = useState<string>('');
  const [last, setLast] = useState<{ text: string; format?: string } | null>(
    null
  );
  const [bundleRoot, setBundleRoot] = useState<string>('');
  const containerRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const scannerRef = useRef<any>(null);
  const dynRef = useRef<DynNS | null>(null);
  const overlayOpenRef = useRef(false);
  const sentRef = useRef(false);

  const buttonStyles: React.CSSProperties = useMemo(
    () => ({
      color: style?.codescanner_button_text_color || 'white',
      background:
        style?.codescanner_button_bg_color ||
        'linear-gradient(to right, #6991F8 0%, #07296a 100%)',
      boxShadow: `0 5px 5px ${style?.codescanner_button_shadow_color || '#FFFFFF'}`,
      border: 'none',
      cursor: 'pointer',
      padding: '12px 24px',
      fontSize: '14px',
      fontWeight: 'bold',
      borderRadius: 4,
      minHeight: 48,
    }),
    [style]
  );

  const scanButtonStyles: React.CSSProperties = useMemo(
    () => ({
      color: style?.codescanner_scan_button_text_color || buttonStyles.color,
      background:
        style?.codescanner_scan_button_bg_color ||
        (buttonStyles.background as string),
      boxShadow: `0 5px 5px ${style?.codescanner_scan_button_shadow_color || '#FFFFFF'}`,
      border: 'none',
      cursor: 'pointer',
      padding: '12px 24px',
      fontSize: '14px',
      fontWeight: 'bold',
      borderRadius: 4,
      minHeight: 48,
    }),
    [style, buttonStyles]
  );

  const uploadButtonStyles: React.CSSProperties = useMemo(
    () => ({
      color: style?.codescanner_upload_button_text_color || buttonStyles.color,
      background:
        style?.codescanner_upload_button_bg_color ||
        (buttonStyles.background as string),
      boxShadow: `0 5px 5px ${style?.codescanner_upload_button_shadow_color || '#FFFFFF'}`,
      border: 'none',
      cursor: 'pointer',
      padding: '12px 24px',
      fontSize: '14px',
      fontWeight: 'bold',
      borderRadius: 4,
      minHeight: 48,
    }),
    [style, buttonStyles]
  );

  const fetchConfig = useCallback(async () => {
    if (!event?.id) return;
    try {
      const url = `${backendBaseUrl}/codescanner/config`;
      console.log('[DBR][CONFIG][GET]', url, { event_id: event.id });
      const resp = await axios.get(url, { params: { event_id: event.id } });
      console.log('[DBR][CONFIG][RESP]', resp.status, resp.data);
      setFieldKey(resp.data.field_key || 'serial_number');
    } catch (e) {
      console.log('[DBR][CONFIG][ERROR]', e);
      setFieldKey('serial_number');
    }
  }, [backendBaseUrl, event?.id]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const isRealBundle = useCallback(async (url: string) => {
    try {
      const r = await fetch(url, { method: 'GET' });
      if (!r.ok) return false;
      const ct = r.headers.get('content-type') || '';
      const text = await r.text();
      if (ct.includes('javascript')) return true;
      return text.includes('Dynamsoft') && text.length > 50000;
    } catch {
      return false;
    }
  }, []);

  const probeRoots = useCallback(async () => {
    const origin = window.location.origin;
    // Try the path that matches the current deployment FIRST, then fall back.
    // - With STATIC_URL set (production): assets live under e.g. /static/dist/.
    // - Without it (dev, Vite base '/'): public assets are served at the root.
    const cands = STATIC_URL
      ? [
          `${origin}${STATIC_URL}/${SDK_DIR}`,
          `${origin}/${SDK_DIR}`,
        ]
      : [
          `${origin}/${SDK_DIR}`,
          `${origin}/static/dist/${SDK_DIR}`,
          `${origin}/static/${SDK_DIR}`,
          `${origin}/dist/${SDK_DIR}`,
        ];
    for (const root of cands) {
      const ok = await isRealBundle(`${root}/dbr.bundle.js`);
      console.log('[DBR][PROBE]', root, ok);
      if (ok) {
        setBundleRoot(root);
        return;
      }
    }
    setBundleRoot('');
  }, [isRealBundle]);

  useEffect(() => {
    probeRoots();
  }, [probeRoots]);

  const lazyImportBundle = useCallback(async (): Promise<any> => {
    const w: any = window;
    if (!bundleRoot) throw new Error('Nessun bundleRoot valido trovato');
    const inject = (src: string) =>
      new Promise<boolean>(resolve => {
        if (document.querySelector(`script[src="${src}"]`))
          return resolve(true);
        const s = document.createElement('script');
        s.src = src;
        s.defer = true;
        s.onload = () => resolve(true);
        s.onerror = () => resolve(false);
        document.head.appendChild(s);
      });
    const src = `${bundleRoot}/dbr.bundle.js`;
    const ok = await inject(src);
    console.log('[DBR][LAZY_IMPORT][SCRIPT]', src, ok);
    const hasScanner = !!w?.Dynamsoft?.BarcodeScanner;
    console.log('[DBR][LAZY_IMPORT][GLOBAL]', Object.keys(w.Dynamsoft || {}), {
      hasScanner,
    });
    if (!ok || !hasScanner)
      throw new Error('Bundle v11 non trovato o BarcodeScanner non esposto');
    w.Dynamsoft.engineResourcePaths = {
      rootDirectory: bundleRoot,
      dbrBundle: bundleRoot,
    };
    console.log('[DBR][ENGINE ROOT]', bundleRoot);
    return w.Dynamsoft;
  }, [bundleRoot]);

  const stop = useCallback(async () => {
    try {
      setStatus('loading');
    } catch {}
    try {
      sentRef.current = false;
    } catch {}
    try {
      await scannerRef.current?.dispose?.();
    } catch {}
    scannerRef.current = null;
    overlayOpenRef.current = false;
    setStatus('idle');
    console.log('[DBR][STOP] done');
  }, []);

  useEffect(
    () => () => {
      if (overlayOpenRef.current) stop();
    },
    [stop]
  );

  const postToBackend = useCallback(
    async (text: string) => {
      if (sentRef.current) return;
      sentRef.current = true;
      setStatus('posting');
      const payload = {
        product_id: product?.id || undefined,
        product_code: product?.code || undefined,
        field_key: fieldKey || 'serial_number',
        value: text,
        screen_type_key: 'codescanner',
      };
      try {
        const url = `${backendBaseUrl}/codescanner/update`;
        console.log('[DBR][POST]', url, payload);
        const resp = await axios.post(url, payload);
        console.log('[DBR][POST][RESP]', resp.status, resp.data);
        const redirectCode = resp?.data?.redirect_product;
        if (redirectCode) {
          const qs = new URLSearchParams(window.location.search);
          const base = `${redirectBaseUrl}/?product=${encodeURIComponent(redirectCode)}&start_index=1`;
          const urlWithToken = qs.get('token')
            ? `${base}&token=${encodeURIComponent(qs.get('token') as string)}`
            : base;
          window.location.href = urlWithToken;
        }
        stop();
        if (!redirectCode) onNext();
      } catch (e) {
        console.log('[DBR][ERROR] post', e);
        setError(style?.codescanner_error_indication || 'Errore invio dato');
        setStatus('ready');
        sentRef.current = false;
      }
    },
    [
      backendBaseUrl,
      fieldKey,
      onNext,
      product?.code,
      product?.id,
      redirectBaseUrl,
      stop,
      style?.codescanner_error_indication,
    ]
  );

  const start = useCallback(async () => {
    try {
      setError('');
      setStatus('loading');
      overlayOpenRef.current = true;
      const Dyn = await lazyImportBundle();
      dynRef.current = Dyn;
      const Scanner = Dyn?.BarcodeScanner;
      if (!Scanner)
        throw new Error('BarcodeScanner non disponibile nel bundle');
      const f128 = Dyn.EnumBarcodeFormat?.BF_CODE_128;
      const f39 = Dyn.EnumBarcodeFormat?.BF_CODE_39;
      const formats = f128 && f39 ? [f128, f39] : undefined;
      const scanner = new Scanner({
        license: LICENSE || '',
        barcodeFormats: formats,
        container: containerRef.current || undefined,
        autoStartCapturing: true,
        scanMode: Dyn.EnumScanMode?.SM_SINGLE,
      });
      scannerRef.current = scanner;
      setStatus('scanning');
      const result = await scanner.launch();
      const item = result?.barcodeResults?.[0];
      const text = item?.text || item?.barcodeText || '';
      const fmt = item?.formatString || item?.barcodeFormatString || '';
      if (text) {
        setLast({ text, format: fmt });
        console.log('[DBR][HIT]', text, `(${fmt})`);
        await postToBackend(text);
      } else {
        setStatus('ready');
        console.log('[DBR][HIT] empty result');
      }
    } catch (e: any) {
      console.log('[DBR][ERROR] start', e);
      setError(e?.message || 'Impossibile avviare lo scanner');
      setStatus('ready');
    }
  }, [lazyImportBundle, postToBackend]);

  const handlePhoto = useCallback(
    async (file: File) => {
      try {
        setError('');
        setStatus('loading');
        const Dyn = dynRef.current || (await lazyImportBundle());
        dynRef.current = Dyn;
        const Scanner = Dyn?.BarcodeScanner;
        if (!Scanner)
          throw new Error('BarcodeScanner non disponibile per photo decode');
        const f128 = Dyn.EnumBarcodeFormat?.BF_CODE_128;
        const f39 = Dyn.EnumBarcodeFormat?.BF_CODE_39;
        const formats = f128 && f39 ? [f128, f39] : undefined;
        const scanner = new Scanner({
          license: LICENSE || '',
          barcodeFormats: formats,
          autoStartCapturing: false,
        });
        scannerRef.current = scanner;
        const dec = await scanner.decode(file);
        const item = Array.isArray(dec?.barcodeResults)
          ? dec.barcodeResults[0]
          : dec?.barcodeResults?.[0];
        const text = item?.text || item?.barcodeText || '';
        const fmt = item?.formatString || item?.barcodeFormatString || '';
        await scanner.dispose?.();
        scannerRef.current = null;
        if (!text) {
          setError(
            style?.codescanner_error_indication || 'Nessun codice rilevato'
          );
          setStatus('ready');
          return;
        }
        setLast({ text, format: fmt });
        await postToBackend(text);
      } catch (e) {
        console.log('[DBR][ERROR] photo', e);
        setError(
          style?.codescanner_error_indication || 'Errore lettura immagine'
        );
        setStatus('ready');
      }
    },
    [lazyImportBundle, postToBackend, style?.codescanner_error_indication]
  );

  const onPickPhoto = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const f = e.target.files?.[0];
      if (!f) return;
      handlePhoto(f);
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [handlePhoto]
  );

  useEffect(() => {
    console.log('[DBR][MOUNT]', {
      ua: navigator.userAgent,
      secure: isSecureContext,
      hasGUM: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
    });
    setStatus('ready');
    return () => {
      console.log('[DBR][UNMOUNT]');
      if (overlayOpenRef.current) stop();
    };
  }, [stop]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 'clamp(8px, 1.5vh, 16px)',
        width: '100%',
        padding: 'clamp(8px, 2vw, 16px)',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 12,
          flexWrap: 'wrap',
          justifyContent: 'center',
        }}
      >
        <button
          onClick={start}
          style={scanButtonStyles}
          disabled={status === 'scanning' || status === 'loading'}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: style?.codescanner_scan_button_indication || 'Scan',
            }}
          />
        </button>
        {/*<button onClick={() => fileInputRef.current?.click()} style={uploadButtonStyles}>
          <span dangerouslySetInnerHTML={{ __html: style?.codescanner_upload_button_indication || 'Upload' }} />
        </button>*/}
      </div>

      {/* DEBUG TOOLS
      <div style={{ fontSize: 12, opacity: 0.8 }}>
        <div>status: {status}</div>
        {bundleRoot && <div>bundleRoot: {bundleRoot}</div>}
        {last && <div>last: {last.text} {last.format ? `(${last.format})` : ''}</div>}
      </div>*/}

      <div
        ref={containerRef}
        style={{
          width: '100%',
          maxWidth: 720,
          height: 'clamp(220px, 50vh, 480px)',
          borderRadius: 8,
          overflow: 'hidden',
          background: '#000',
        }}
      />

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={onPickPhoto}
      />

      {error && (
        <div
          style={{
            backgroundColor: '#ffe6e6',
            border: '2px solid #ff4444',
            borderRadius: 8,
            padding: 16,
            maxWidth: 400,
            width: '90%',
            textAlign: 'center',
          }}
        >
          <div
            style={{ marginBottom: 8 }}
            dangerouslySetInnerHTML={{ __html: error }}
          />
          <button
            onClick={() => setError('')}
            style={{
              backgroundColor: '#ff4444',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              padding: '8px 16px',
              cursor: 'pointer',
            }}
          >
            OK
          </button>
        </div>
      )}
      {showBackButton && (
        <button
          type="button"
          onClick={() => onBack?.()}
          style={{
            fontSize: '18px',
            textDecoration: 'underline',
            color: style?.back_button_text_color || '#07296A',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            marginTop: '20px',
          }}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: style?.back_button_indication || 'Back',
            }}
          />
        </button>
      )}
      {showSkipNextScreenButton && (
        <button
          type="button"
          onClick={onNext}
          style={{
            fontSize: '18px',
            textDecoration: 'underline',
            color: style?.skip_next_screen_button_text_color || '#07296A',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            marginTop: '20px',
          }}
        >
          <span
            dangerouslySetInnerHTML={{
              __html: style?.skip_next_screen_button_indication || 'Skip',
            }}
          />
        </button>
      )}
    </div>
  );
}
